import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, Upload, Sparkles, Receipt } from "lucide-react";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

export default function InvoiceUploadModal({ open, onClose, workspaceId, engagementId, onCreated }) {
  const fileInput = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    number: "", vendor: "", amount: "", currency: "GBP",
    issue_date: "", due_date: "", notes_md: "", status: "draft",
  });

  const handleParse = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/invoices/parse`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Parse failed (${r.status})`);
      }
      const d = await r.json();
      if (!d.ok) {
        throw new Error("Rex couldn't parse this — fill manually");
      }
      const p = d.parsed || {};
      setDraft({
        number: p.number || draft.number,
        vendor: p.vendor || draft.vendor,
        amount: p.amount ?? draft.amount,
        currency: (p.currency || "GBP").toUpperCase(),
        issue_date: p.issue_date || "",
        due_date: p.due_date || "",
        notes_md: p.notes || draft.notes_md,
        status: "draft",
        line_items: p.line_items || [],
      });
      toast.success("Rex extracted the invoice — review and save");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setParsing(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const save = async () => {
    const amt = Number(draft.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Amount must be a positive number");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...draft,
        amount: amt,
        engagement_id: engagementId || null,
        issue_date: draft.issue_date || null,
        due_date: draft.due_date || null,
      };
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/invoices`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Failed to save invoice");
      const inv = await r.json();
      toast.success("Invoice saved");
      onCreated?.(inv);
      onClose?.();
      setDraft({ number: "", vendor: "", amount: "", currency: "GBP", issue_date: "", due_date: "", notes_md: "", status: "draft" });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="invoice-upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-emerald-600" /> Add invoice</DialogTitle>
          <DialogDescription>Upload a PDF or image and Rex will extract the fields, or fill them manually.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 flex items-center justify-between gap-3 bg-slate-50/40">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Sparkles className="h-4 w-4 text-blue-600" /> Parse with Rex (Gemini 2.5 Pro)
            </div>
            <input ref={fileInput} type="file" accept=".pdf,image/*" onChange={handleParse} className="hidden" data-testid="invoice-file-input" />
            <Button size="sm" variant="outline" onClick={() => fileInput.current?.click()} disabled={parsing} data-testid="invoice-upload-btn">
              {parsing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
              {parsing ? "Parsing…" : "Upload"}
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-600">Invoice number</Label>
              <Input data-testid="inv-number" value={draft.number} onChange={(e) => setDraft({ ...draft, number: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Vendor</Label>
              <Input data-testid="inv-vendor" value={draft.vendor} onChange={(e) => setDraft({ ...draft, vendor: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Amount *</Label>
              <Input data-testid="inv-amount" type="number" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Currency</Label>
              <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
                {["GBP", "USD", "NGN", "EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Issue date</Label>
              <Input type="date" value={draft.issue_date} onChange={(e) => setDraft({ ...draft, issue_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Due date</Label>
              <Input type="date" value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Status</Label>
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
                {["draft", "sent", "paid", "overdue", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Notes</Label>
            <Textarea rows={2} value={draft.notes_md} onChange={(e) => setDraft({ ...draft, notes_md: e.target.value })} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || parsing} data-testid="invoice-save-btn">
            {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            Save invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
